import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';

import { QuestionnaireService } from '../../core/services/questionnaire.service';
import { MessageService } from 'primeng/api';
import { FileValidationService } from '../../core/services/file-validation.service';

import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { Toast } from 'primeng/toast';
import { CardModule } from 'primeng/card';
import { FileUploadModule } from 'primeng/fileupload';

@Component({
  selector: 'app-checklist-run-user',
  standalone: true,
  imports: [
    CommonModule,
    ProgressSpinnerModule,
    ButtonModule,
    TagModule,
    Toast,
    CardModule,
    FileUploadModule,
  ],
  providers: [MessageService],
  templateUrl: './checklist-run-user.component.html',
  styleUrls: ['./checklist-run-user.component.scss'],
})
export class ChecklistRunUserComponent implements OnInit {
  private readonly service = inject(QuestionnaireService);
  private readonly messageService = inject(MessageService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fileValidationService = inject(FileValidationService);
  private readonly titleService = inject(Title); // WCAG 2.4.2: Page Title

  checklist = signal<any>(null);
  isLoading = signal(true);
  selectedFiles = signal<Record<string, File | null>>({});

  isSaveEnabled = computed(() =>
    Object.values(this.selectedFiles()).some((file) => file instanceof File),
  );

  ngOnInit(): void {
    // WCAG 2.4.2: Set descriptive page title
    this.titleService.setTitle('Compilazione Checklist - Portale Fiduciari AXA');
    this.loadChecklist();
  }

  private loadChecklist(): void {
    const id = this.route.snapshot.paramMap.get('id');

    if (!id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: "ID Checklist mancante nell'URL",
      });
      this.isLoading.set(false);
      return;
    }

    this.service.getChecklistById(id).subscribe({
      next: (data) => {
        this.checklist.set(data);
        this.isLoading.set(false);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Caricamento fallito',
        });
        this.isLoading.set(false);
      },
    });
  }

  getItemKey(item: any, index: number): string {
    if (item?.id) return String(item.id);
    if (item?.position) return String(item.position);
    return String(index);
  }

  async onFileSelected(event: any, item: any, index: number): Promise<void> {
    const selected = event?.files?.[0] ?? null;
    const itemKey = this.getItemKey(item, index);

    if (!selected || !(selected instanceof File)) {
      this.selectedFiles.update((current) => ({
        ...current,
        [itemKey]: null,
      }));
      return;
    }

    // 🔒 Validazione completa del file con FileValidationService
    // Supporta: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, JPG, JPEG, PNG
    const validation = await this.fileValidationService.validateFileChecklist(selected);
    if (!validation.valid) {
      this.messageService.add({
        severity: 'error',
        summary: '🔒 Validazione File Fallita',
        detail: validation.error || 'Il file non ha superato i controlli di sicurezza',
        life: 6000
      });

      // Log dettagli per debugging
      if (validation.details) {
        console.warn('File validation failed:', validation.details);
      }

      // Reset file selection
      event.files = [];
      this.selectedFiles.update((current) => ({
        ...current,
        [itemKey]: null,
      }));
      return;
    }

    // File valido - salva
    this.selectedFiles.update((current) => ({
      ...current,
      [itemKey]: selected,
    }));

    this.messageService.add({
      severity: 'success',
      summary: 'File caricato',
      detail: `File "${selected.name}" caricato con successo`,
      life: 3000
    });
  }

  getSelectedFileName(item: any, index: number): string | null {
    const itemKey = this.getItemKey(item, index);
    return this.selectedFiles()[itemKey]?.name || null;
  }

  saveChecklist(): void {
    if (!this.isSaveEnabled()) {
      return;
    }

    this.messageService.add({
      severity: 'success',
      summary: 'Salvataggio completato',
      detail: 'File selezionati correttamente',
    });

    setTimeout(() => this.router.navigate(['/list-file-checklist']), 1000);
  }
}
